<template>
  <div class="competence-input">
    <a-textarea
      v-if="disabled"
      :value="competenceName ? `#${competenceName}` : ''"
      :disabled="disabled"
      :class="` ${
        !!competenceStatus
          ? competenceStatus !== 'default'
            ? competenceStatus === 'confirmed'
              ? 'competence__confirmed'
              : 'competence__rejected'
            : 'competence__default'
          : ''
      }`"
      :auto-size="{ maxRows: 2 }"
    />
    <a-select
      v-else
      :placeholder="placeholder"
      :value="competenceName"
      :disabled="disabled"
      :options="options"
      :showArrow="false"
      :loading="isLoading"
      @select="handleSelect"
      @search="searchItems"
      @input-key-down="onPressKey"
      @deselect="handleDeselect"
      show-search
      allowClear
    />
  </div>
</template>

<script setup>
  import { onMounted, ref, watch } from 'vue';
  import { debounce } from 'lodash-es';
  import { useApi } from '@/api/use-api';

  const props = defineProps({
    type: {
      type: String,
      required: true,
    },
    competence: {
      type: Object,
      default: null,
    },
    competenceStatus: {
      type: String,
      default: null,
    },
    competenceOptions: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Введите компетенцию',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const api = useApi();
  const emit = defineEmits(['update', 'delete', 'input-click']);
  const selectedItem = ref(null);
  const competenceName = ref('');
  const options = ref([]);
  const isLoading = ref(true);

  onMounted(async () => {
    setDefaultOptions();
  });

  watch(
    () => props.competenceOptions,
    () => {
      setDefaultOptions();
    }
  );

  watch(
    () => props.competence,
    () => {
      if (props.competence) {
        competenceName.value = props.competence.name;
      }
    },
    { deep: true }
  );

  const onPressKey = (event) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();

      const newItemName = competenceName.value?.trim();
      if (newItemName !== '' && options.value.length === 0) {
        handleSelect(newItemName);
      }
    }
  };

  const setDefaultOptions = () => {
    options.value = props.competenceOptions.reduce((acc, group) => {
      const options = group.options.filter((el) => el.type === props.type);
      return options.length > 0
        ? [...acc, { label: group.label, options: options }]
        : acc;
    }, []);
  };

  const searchItems = debounce(async (value) => {
    if (!value) return;

    competenceName.value = value;
    isLoading.value = true;
    try {
      const query = new URLSearchParams();

      if (value) {
        query.set('name', value);
      } else {
        query.set('isMain', '1');
        query.set('per-page', '50');
      }
      query.set('type', props.type);

      const response = await api.competence.index(query);
      options.value = getOptions(response.competencies.items);

      handleSelect(value);
    } catch (error) {
      console.log(error);
    } finally {
      isLoading.value = false;
    }
  }, 300);

  const getOptions = (competencies) => {
    return competencies.map((el) => ({ value: el.name, ...el }));
  };

  const handleSelect = (value) => {
    competenceName.value = value;

    const selectedOption = props.competenceOptions
      .reduce((acc, el) => [...acc, ...el.options], [...options.value])
      .find((el) => el.name === value);

    selectedItem.value = {
      id: selectedOption?.id,
      type: props.type,
      name: value,
      isMain: selectedOption?.isMain ?? false,
    };

    emit('update', selectedItem.value);
  };

  const handleDeselect = () => {
    selectedItem.value = null;
    competenceName.value = '';
    setDefaultOptions();
    emit('delete', props.type);
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .competence-input {
    .ant-input-disabled {
      cursor: default;
      color: $color-text-primary;
      font-family: Nunito Sans, sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;

      &.competence {
        &__default {
          background-color: $color-text-secondary-white;
          border: 0;
        }

        &__confirmed {
          background-color: rgba(114, 200, 30, 0.25);
          border: 1px solid $color-bg-status-done;
        }

        &__rejected {
          background-color: rgba(246, 81, 96, 0.1);
          border: 1px solid $color-text-accent-red;
        }
      }
    }
  }
</style>
